import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown, Pin, PinOff, X } from "@fg/icons";
import type { PinScope, PinnedMessage } from "@/api/connectApi";
import { usePinError, usePinToggle, usePins } from "@/api/hooks";
import { cn } from "@/lib/utils";

function when(iso: string): string {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/**
 * The "Pinned" strip under a channel or chat header (Teams): how many
 * messages are pinned and the latest one; open it for the full list — jump to
 * a message, or unpin it if you're allowed to. Also shows a pin error (e.g.
 * the 25-pin limit) raised by a pin action anywhere in this conversation.
 */
export function PinnedBar({ scope, id, onJump }: {
    scope: PinScope;
    id: string;
    onJump: (pin: PinnedMessage) => void;
}) {
    const pins = usePins(scope, id);
    const toggle = usePinToggle(scope, id);
    const pinError = usePinError(scope, id);
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const listId = useId();

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const list = pins.data?.pins ?? [];
    const canPin = pins.data?.canPin ?? false;
    if (list.length === 0 && !pinError.message) return null;
    const latest = list[0];

    return (
        <div ref={rootRef} className="relative shrink-0 border-b bg-amber-50 dark:bg-amber-950/40">
            {pinError.message ? (
                <div role="alert" className="flex items-center gap-2 border-b border-amber-200 px-4 py-1.5 text-xs text-destructive dark:border-amber-900">
                    <span className="min-w-0 flex-1">{pinError.message}</span>
                    <button type="button" onClick={pinError.clear} aria-label="Dismiss" className="rounded p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900">
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            ) : null}
            {latest ? (
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    aria-controls={listId}
                    className="flex w-full min-w-0 items-center gap-2 px-4 py-1.5 text-left text-xs hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary dark:hover:bg-amber-950"
                >
                    <Pin className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                    <span className="shrink-0 font-medium">{list.length} pinned</span>
                    <span className="min-w-0 flex-1 truncate text-muted-foreground">
                        <span className="font-medium text-foreground/80">{latest.authorName}:</span> {latest.snippet}
                    </span>
                    <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform", open && "rotate-180")} aria-hidden />
                </button>
            ) : null}
            {open && list.length > 0 ? (
                <ul
                    id={listId}
                    aria-label="Pinned messages"
                    className="absolute left-0 right-0 top-full z-30 max-h-96 overflow-auto border-b bg-popover text-popover-foreground shadow-lg"
                >
                    {list.map((p) => (
                        <li key={p.messageId} className="flex items-start gap-1 border-b last:border-b-0">
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    onJump(p);
                                }}
                                className="flex min-w-0 flex-1 items-start gap-3 px-4 py-2 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                            >
                                <Pin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                                <span className="min-w-0 flex-1">
                                    <span className="flex flex-wrap items-baseline gap-x-2 text-xs">
                                        <span className="font-medium">{p.authorName}</span>
                                        <time dateTime={p.sentAt} className="text-muted-foreground">{when(p.sentAt)}</time>
                                        {p.parentMessageId ? <span className="text-muted-foreground">· reply in a thread</span> : null}
                                    </span>
                                    <span className="mt-0.5 line-clamp-2 break-words text-sm">{p.snippet || "(no text)"}</span>
                                    {p.pinnedByName ? (
                                        <span className="mt-0.5 block text-[10px] text-muted-foreground">Pinned by {p.pinnedByName}</span>
                                    ) : null}
                                </span>
                            </button>
                            {canPin ? (
                                <button
                                    type="button"
                                    onClick={() => toggle.mutate({ messageId: p.messageId, pin: false })}
                                    disabled={toggle.isPending}
                                    aria-label={`Unpin message from ${p.authorName}`}
                                    title="Unpin"
                                    className="m-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                                >
                                    <PinOff className="h-3.5 w-3.5" />
                                </button>
                            ) : null}
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}
